import dayjs from "dayjs";
import { GetAppointment, GetSchedule } from "@/services/fhir";
import { TELECONSULTORIA_SERVICE_CATEGORY_TOKEN } from "../../constants";
import { actorByType, isTeleconsultoriaAgendaSchedule } from "../../utils/actors";
import { schedulesFromBundle } from "../../services/teleconsultoriaScheduleService";
import { patientsByIdFrom, toBookedRow } from "../transformers/bookedRowTransformers";
import type { BookedTeleconsultoriaRow } from "../types";

/** Mesmos status do FutureViewer (dashboard). */
const BOOKED_STATUSES: Array<"booked" | "pending"> = ["booked", "pending"];
const SCHEDULE_SEARCH_COUNT = 100;

/** Agendas de teleconsultoria COM horários da organização (opcional: de um profissional). */
async function listAgendaSchedules(params: {
  organizationToken: string;
  practitionerReference?: string;
}): Promise<fhir5.Schedule[]> {
  const bundle = await GetSchedule({
    organization: params.organizationToken,
    serviceCategory: TELECONSULTORIA_SERVICE_CATEGORY_TOKEN,
    count: SCHEDULE_SEARCH_COUNT,
    ...(params.practitionerReference && { practitioner: params.practitionerReference }),
  });
  return schedulesFromBundle(bundle).filter(
    (schedule) =>
      isTeleconsultoriaAgendaSchedule(schedule) &&
      (!params.practitionerReference ||
        actorByType(schedule.actor, "Practitioner")?.reference === params.practitionerReference),
  );
}

/**
 * Teleconsultorias marcadas nas agendas de teleconsultoria.
 * Mesma busca do FutureViewer: `slot.schedule` + `actor=Practitioner` dos donos
 * das agendas + `includePatient`. Sem o actor o scheduling responde 409.
 * Período opcional (YYYY-MM-DD); sem ele, de hoje em diante (como antes).
 */
export async function listBookedTeleconsultorias(params: {
  organizationToken: string;
  practitionerReference?: string;
  startDate?: string;
  endDate?: string;
}): Promise<BookedTeleconsultoriaRow[]> {
  if (!params.organizationToken) return [];
  const schedules = await listAgendaSchedules(params);
  if (!schedules.length) return [];

  const ownerReferenceList = [
    ...new Set(
      schedules
        .map((schedule) => actorByType(schedule.actor, "Practitioner")?.reference)
        .filter((reference): reference is string => Boolean(reference)),
    ),
  ];
  if (!ownerReferenceList.length) return [];

  const bundle = await GetAppointment({
    schedule: schedules.map((schedule) => schedule.id).join(","),
    startDate: params.startDate || dayjs().format("YYYY-MM-DD"),
    ...(params.endDate && { endDate: params.endDate }),
    status: BOOKED_STATUSES,
    includePatient: true,
    scheduling: true,
    professionalActor: ownerReferenceList.join(","),
  });

  const resources = ((bundle as fhir5.Bundle | undefined)?.entry || []).map(
    (entry) => entry.resource,
  );
  const patientsById = patientsByIdFrom(resources);
  // Teleconsultor: o Practitioner do Appointment que é dono de uma das agendas.
  const ownerReferences = new Set(ownerReferenceList);
  return resources
    .filter((resource): resource is fhir5.Appointment => resource?.resourceType === "Appointment")
    .map((appointment) => toBookedRow(appointment, { patientsById, ownerReferences }))
    .filter((row): row is BookedTeleconsultoriaRow => Boolean(row));
}
